<template>
    <div class="mx-auto w-full max-w-[1168px] pb-40 px-8 pt-6">
        <template v-if="state==='loaded'">
            <ui-header :title="`Dayworks Sheet ${dayworksSheet.referenceNumber}`"
                       :subtitle="'ID #'+this.dayworksSheet.id">
                <template #actions>
                    <ui-button-new icon="fas fa-arrow-left" @click="finished()">Done</ui-button-new>
                </template>
            </ui-header>

            <!-- General Info -->
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                <div>
                    <ui-date-picker label="Date" name="dateTime" v-model="dayworksSheet.date"
                                    class="xl:col-span-6"
                                    placeholder="What is the date?"
                                    :error="dateTimeError"
                                    :with-time="false"
                                    @input="dateTimeError='';save();"></ui-date-picker>
                </div>
                <div>
                    <label class="block text-sm font-medium text-gray-700">Location of Works</label>
                    <input type="text" placeholder="e.g. Plot 12 - Main Roof" v-model="dayworksSheet.locationOfWorks"
                           @input="save"
                           class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm">
                </div>
                <div>
                    <label class="block text-sm font-medium text-gray-700">Name</label>
                    <input type="text" placeholder="e.g. John Smith" v-model="dayworksSheet.name" @input="save"
                           class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm">
                </div>
            </div>

            <!-- Works Description -->
            <div class="mt-4">
                <ui-textarea autoresize label="Description of Works" name="descriptionOfWorks"
                             v-model="dayworksSheet.descriptionOfWorks"
                             placeholder="e.g. Installed additional lead flashings..."
                             @input="save"
                             class=""></ui-textarea>
            </div>

            <!-- Impact on Programme -->
            <div class="mt-4">
                <label class="block text-sm font-medium text-gray-700 mb-1">Will this impact the programme?</label>
                <div class="flex items-center space-x-6 mt-1">
                    <label class="inline-flex items-center text-sm text-gray-700">
                        <input type="radio" name="impactProgramme" :value="true"
                               v-model="dayworksSheet.willImpactProgramme"
                               @input="save"
                               class="text-blue-600 border-gray-300 focus:ring-blue-500">
                        <span class="ml-2">Yes</span>
                    </label>
                    <label class="inline-flex items-center text-sm text-gray-700">
                        <input type="radio" name="impactProgramme" :value="false"
                               v-model="dayworksSheet.willImpactProgramme"
                               @input="save"
                               class="text-blue-600 border-gray-300 focus:ring-blue-500">
                        <span class="ml-2">No</span>
                    </label>
                </div>
            </div>

            <!-- Labour, Plant, Materials Table -->
            <div class="mt-6">
                <h2 class="text-md font-semibold text-gray-700 mb-2">Dayworks Breakdown</h2>
                <div
                    class="flex mt-4 border-t border-b border-gray-200 bg-gray-50 text-xs text-gray-500 font-semibold">
                    <div class="w-6 flex-shrink-0"></div>
                    <div class="border-l border-r border-gray-200 px-2 py-1 w-1/4">Type</div>
                    <div class="border-r border-gray-200 px-2 py-1 flex-grow">Description</div>
                    <div class="-ml-px border-r border-gray-200 px-2 py-1 w-1/4">Qty</div>
                    <div class="w-6 flex-shrink-0 ml-2"></div>
                </div>
                <draggable :list="dayworksSheet.breakdown" draggable=".draggable" handle=".handle" group="breakdown"
                           @change="save"
                >
                    <dayworks-breakdown-item v-for="(item,key) in dayworksSheet.breakdown" :key="item.index"
                                             ref="item"
                                             :item="item"
                                             @removeItem="removeBreakdownItem"
                                             @changed="save"
                    ></dayworks-breakdown-item>
                </draggable>
                <!-- New Task Item. -->
                <div class="flex border-t border-b -mt-px border-gray-200 draggable bg-white">
                    <!-- Draggable handle. -->
                    <div class="w-6 flex-shrink-0 self-start py-1.5 -my-px border-t">
                        <i class="fa fa-plus text-xs text-gray-300 pl-1 pt-1"></i>
                    </div>
                    <div class="flex-1 ml-1 flex-grow">
                        <input
                            placeholder="Add a new item..."
                            class="text-sm -ml-px -my-px px-2 py-1.5 border border-gray-300 shadow-none relative hover:z-10 focus:z-30 resize-none bg-white"
                            type="text"
                            v-model="newItem"
                            @input="addItem"
                        >
                    </div>
                    <div class="w-6 flex-shrink-0 ml-2"></div>
                </div>
            </div>
            <div class="w-full mb-2 flex mt-6">
                <div class="flex-grow">
                    <ui-input-file-inline label="Image, Videos and PDFs"
                                          name="media[]"
                                          :filesystemId="dayworksSheet.filesystemId"
                                          :readonly="false"
                                          :attachUrl="attachUrl"
                                          :detachUrl="detachUrl"
                                          :downloadUrl="downloadUrl"
                                          :current-files="getDayworksFiles"></ui-input-file-inline>
                </div>
            </div>
            <ui-data-grid-item class="mt-8" label="Quick action" wide
                               v-if="dayworksSheet.status==='Draft' || dayworksSheet.status==='Sent'">
                <div class="flex flex-col md:flex-row flex-wrap -m-2">
                    <ui-button-new class="m-2" icon="fas fa-route" extra
                                   @click="sendForApproval"
                    >Finish & Send for Approval
                    </ui-button-new>
                </div>
            </ui-data-grid-item>

            <ui-data-grid class="mt-6">
                <ui-data-grid-item label="Created By">{{ dayworksSheet.createdBy }} on {{ dayworksSheet.createdOn }}
                </ui-data-grid-item>
                <ui-data-grid-item label="Last Updated By" v-if="dayworksSheet.lastUpdatedBy">{{
                        dayworksSheet.lastUpdatedBy
                    }} on {{
                        dayworksSheet.lastUpdatedOn
                    }}
                </ui-data-grid-item>
            </ui-data-grid>

        </template>
    </div>
</template>

<script>
import UiContainer from "../../ui/UiContainer.vue";
import UiDatePicker from "../../ui/UiDatePicker.vue";
import UiButtonNew from "../../ui/UiButtonNew.vue";
import UiDataGridItem from "../../ui/UiDataGridItem.vue";
import UiHeader from "../../ui/UiHeader.vue";
import UiInput from "../../ui/UiInput.vue";
import UiToggle from "../../ui/UiToggle.vue";
import moment from "moment";
import {debounce} from "lodash";
import {DayworksSheet} from "@models";
import UiSelect from "../../ui/UiSelect.vue";
import DayworksBreakdownItem from "./DayworksBreakdownItem.vue";
import {uuid} from "../../../utils";
import draggable from 'vuedraggable'
import UiDropdownItem from "../../ui/UiDropdownItem.vue";
import UiDropdown from "../../ui/UiDropdown.vue";
import {readonly} from "vue";
import UiDataGrid from "../../ui/UiDataGrid.vue";

export default {
    components: {
        UiDataGrid,
        UiDropdown, UiDropdownItem,
        draggable,
        DayworksBreakdownItem,
        UiSelect,
        UiToggle, UiInput, UiHeader, UiDataGridItem, UiDatePicker, UiButtonNew, UiContainer
    },
    props: {
        item: Object,
        packageReference: null,
        fsModal: Object,
        setEditMode: Function,
    },
    data() {
        return {
            dayworksSheet: {},
            busy: false,
            dateTimeError: '',
            moment,
            state: 'loading',
            debouncedUpdate: debounce(this.update, 300),
            newItem: '',
        }
    },
    computed: {},
    methods: {
        readonly,
        async sendForApproval() {
            this.save();
            this.$emit('finishAndSend');
        },
        attachUrl(id) {
            return route('api.v2.project-delivery.dayworks-sheet.file.attach', {
                'package_reference': this.packageReference,
                'id': this.item.id,
                'file_id': id
            })
        },
        detachUrl(id) {
            return route('api.v2.project-delivery.dayworks-sheet.file.detach', {
                'package_reference': this.packageReference,
                'id': this.item.id,
                'file_id': id
            })
        },
        downloadUrl(id) {
            return route('api.v2.project-delivery.dayworks-sheet.file.download', {
                'package_reference': this.packageReference,
                'id': this.item.id,
                'file_id': id
            })
        },
        async getDayworksFiles() {
            let data = await DayworksSheet.find(this.packageReference, this.item.id)
            return data.files
        },
        async save() {
            this.dateTimeError = '';
            if (!this.dayworksSheet.date || this.dayworksSheet.date === "Invalid date") {
                this.dateTimeError = 'Please select a date of issue for this Dayworks Sheet'
            } else {
//                let o = await DayworksSheet.find(this.dayworksSheet.packageReference, this.dayworksSheet.id)
                this.debouncedUpdate();
            }
        },
        addItem() {
            this.dayworksSheet.breakdown.push({
                index: uuid(),
                type: 'Labour',
                description: this.newItem,
                quantity: ''
            })
            this.newItem = ''
            this.$nextTick(() => {
                this.$refs.item[this.$refs.item.length - 1].$refs.description.$el.focus();
            })
            this.save()
        },
        removeBreakdownItem(item) {
            this.dayworksSheet.breakdown.splice(this.dayworksSheet.breakdown.findIndex((e) => e.index === item.index), 1);
            this.save()
        },
        async update() {
            this.busy = true;
            this.dayworksSheet.date = moment(this.dayworksSheet.date).format("YYYY-MM-DD");
            await this.dayworksSheet.update();
            Object.assign(this.item, await DayworksSheet.find(this.packageReference, this.item.id));
            this.busy = false;
        },
        showFS() {
            this.fsModal.openModal(this.dayworksSheet.filesystemId);
        },
        uploadToFs() {
            this.fsModal.upload(this.dayworksSheet.filesystemId);
        },
        minDate() {
            return null;
        },
        finished() {
            this.setEditMode(false)
        }
    },
    async mounted() {
        this.dayworksSheet = await DayworksSheet.find(this.packageReference, this.item.id);
        this.dayworksSheet.breakdown.forEach((i) => {
            i.quantity = parseFloat(i.quantity) ? i.quantity : ''
        });
        this.state = 'loaded'
    },
}
</script>
